import type { AppNavGroup, AppNavItem, AppNavLink } from "@homebound/beam";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import {
  developmentCommitmentsPath,
  developmentsPath,
  personalDashboardPaths,
  projectsPath,
  tradePartnersPath,
} from "src/routes/routesDef";
import {
  createBidPackagesUrl,
  createDesignPackagesUrl,
  createGlobalOptionsLegacyUrl,
  createGlobalOptionsUrl,
  createItemCatalogUrl,
  createMaterialCatalogUrl,
  createMilestoneCatalogUrl,
  createPlanPackagesUrl,
  createProductOfferingsUrl,
  createScheduleTemplatesUrl,
  createTaskCatalogUrl,
  createTradesBidContractsUrl,
  createTradesPurchaseOrdersUrl,
  createTradesTradePartnersUrl,
} from "src/RouteUrls";
import { changeLogRoute } from "src/routing/domains/changeLogPaths";
import {
  billsRoute,
  invoiceTermsRoute,
  invoicesRoute,
  lienWaiversRoute,
  purchaseOrdersRoute,
} from "src/routing/domains/financesPaths";
import { warrantyRoute } from "src/routing/domains/warrantyPaths";

export function buildGlobalNavItems(
  currentPath: string,
  featureIsEnabled: (flag: FeatureFlagType) => boolean,
): AppNavItem[] {
  const financesPaths = [
    billsRoute.path,
    invoicesRoute.path,
    purchaseOrdersRoute.path,
    invoiceTermsRoute.path,
    lienWaiversRoute.path,
  ];

  return [
    navLink("Dashboard", personalDashboardPaths.base, currentPath),
    navLink("Projects", [projectsPath, developmentsPath], currentPath, projectsPath),
    navLink("Finances", financesPaths, currentPath, billsRoute.path),
    buildLibrariesNavGroup(currentPath, featureIsEnabled),
    ...(!featureIsEnabled(FeatureFlagType.ProductConfig)
      ? [navLink("Trade Partners", tradePartnersPath, currentPath)]
      : []),
    ...(featureIsEnabled(FeatureFlagType.ProductConfig) ? [buildTradesNavGroup(currentPath)] : []),
    navLink("Group Commitments", developmentCommitmentsPath, currentPath),
    ...(featureIsEnabled(FeatureFlagType.ChangeRequestMvp)
      ? [navLink("Change Log", changeLogRoute.path, currentPath)]
      : []),
    navLink("Warranty", warrantyRoute.path, currentPath),
  ];
}

function navLink(
  label: string,
  path: string | string[],
  currentPath: string,
  href: string = Array.isArray(path) ? path[0] : path,
): AppNavLink {
  return { label, onClick: href, active: isPathActive(currentPath, path) };
}

function groupLink(label: string, url: string, currentPath: string): AppNavLink {
  return { label, onClick: url, active: currentPath.startsWith(url) };
}

function buildLibrariesNavGroup(
  currentPath: string,
  featureIsEnabled: (flag: FeatureFlagType) => boolean,
): AppNavGroup {
  const isProductConfigEnabled = featureIsEnabled(FeatureFlagType.ProductConfig);
  const isMaterialsCatalogEnabled = featureIsEnabled(FeatureFlagType.MaterialCatalog);
  const isTaskCatalogEnabled = featureIsEnabled(FeatureFlagType.DynamicSchedules);
  const isOptionsCatalogEnabled = featureIsEnabled(FeatureFlagType.OptionsCatalogV2);

  const catalogItems: AppNavLink[] = [
    groupLink("Options - Legacy", createGlobalOptionsLegacyUrl(), currentPath),
    ...(isOptionsCatalogEnabled ? [groupLink("Options", createGlobalOptionsUrl(), currentPath)] : []),
    ...(isMaterialsCatalogEnabled ? [groupLink("Materials", createMaterialCatalogUrl(), currentPath)] : []),
    ...(isTaskCatalogEnabled ? [groupLink("Tasks", createTaskCatalogUrl(), currentPath)] : []),
    ...(isTaskCatalogEnabled ? [groupLink("Milestones", createMilestoneCatalogUrl(), currentPath)] : []),
  ];

  const templateItems: AppNavLink[] = [
    groupLink("Bid Items", createItemCatalogUrl(), currentPath),
    groupLink("Schedule Template", createScheduleTemplatesUrl(), currentPath),
  ];

  return {
    label: "Libraries",
    items: [
      ...(isProductConfigEnabled
        ? [
            {
              section: true as const,
              items: [
                groupLink("Plans", createPlanPackagesUrl(), currentPath),
                groupLink("Design Packages", createDesignPackagesUrl(), currentPath),
                groupLink("Product Offerings", createProductOfferingsUrl(), currentPath),
              ],
            },
          ]
        : []),
      { section: true, items: catalogItems },
      { section: true, items: templateItems },
    ],
  };
}

function buildTradesNavGroup(currentPath: string): AppNavGroup {
  return {
    label: "Trades",
    items: [
      groupLink("Bids", createBidPackagesUrl(), currentPath),
      groupLink("Contracts", createTradesBidContractsUrl(), currentPath),
      groupLink("Purchase Orders", createTradesPurchaseOrdersUrl(), currentPath),
      groupLink("Trade Index", createTradesTradePartnersUrl(), currentPath),
    ],
  };
}

function isPathActive(currentPath: string, path: string | string[]): boolean {
  const paths = Array.isArray(path) ? path : [path];
  return paths.some((p) => currentPath.startsWith(p));
}
